import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Option,
  Spinner,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePortfolios } from "apps/project-iq/hooks/usePortfolios";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";
import { toUserOption } from "utils/generalUtils";
import { usePortfoliosPracticesAccessStyles } from "./PortfoliosPracticesAccess.styles";
import { IPortfolioViewerAccessDialogProps } from "./PortfoliosPracticesAccess.types";

export const PortfolioViewerAccessDialog = ({
  isOpen,
  isSaving,
  editingItem = null,
  onSave,
  onDismiss,
}: IPortfolioViewerAccessDialogProps): JSX.Element => {
  const styles = usePortfoliosPracticesAccessStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const dropdownStyles = useSharedDropdownStyles();

  const { portfolios, isLoading: isPortfoliosLoading } = usePortfolios();

  const [selectedUser, setSelectedUser] = useState<CustomOption[]>([]);
  const [portfolioIds, setPortfolioIds] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setSelectedUser(
        editingItem
          ? [
              toUserOption(
                editingItem.user.userId,
                editingItem.user.displayName,
              ),
            ]
          : [],
      );
      setPortfolioIds(editingItem?.portfolioIds ?? []);
      setFieldErrors({});
    }
  }, [isOpen, editingItem]);

  const sortedPortfolios = useMemo(
    () => [...portfolios].sort((a, b) => a.label.localeCompare(b.label)),
    [portfolios],
  );

  const handleSelectedUserChange = useCallback(
    (options: CustomOption[]): void => {
      const lastSelected = options.length ? [options[options.length - 1]] : [];
      setSelectedUser(lastSelected);
      setFieldErrors((prev) => ({ ...prev, user: "" }));
    },
    [],
  );

  const handlePortfolioIdsChange = useCallback(
    (newPortfolioIds: string[]): void => {
      setPortfolioIds(newPortfolioIds);
      setFieldErrors((prev) => ({ ...prev, portfolios: "" }));
    },
    [],
  );

  const handleSave = async (): Promise<void> => {
    const errors: Record<string, string> = {};
    if (selectedUser.length === 0) errors.user = "A user is required";
    if (portfolioIds.length === 0)
      errors.portfolios = "At least one portfolio is required";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      await onSave({
        user: {
          userId: selectedUser[0].id ?? selectedUser[0].key,
          displayName: selectedUser[0].text,
        },
        portfolioIds,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save";
      setFieldErrors((prev) => ({ ...prev, general: errorMessage }));
    }
  };

  const isSaveDisabled =
    isSaving || selectedUser.length === 0 || portfolioIds.length === 0;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, d) => !d.open && !isSaving && onDismiss()}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
                disabled={isSaving}
              />
            }
          >
            {editingItem
              ? "Edit portfolio viewer access"
              : "Add portfolio viewer access"}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              {isSaving && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving portfolio viewer access..." />
                </div>
              )}
              {fieldErrors.general && (
                <div role="alert" className={styles.errorAlert}>
                  {fieldErrors.general}
                </div>
              )}
              <Field
                label="User"
                required
                validationState={fieldErrors.user ? "error" : "none"}
                validationMessage={fieldErrors.user}
                className={styles.dialogField}
              >
                <PeoplePicker
                  selectedOptions={selectedUser}
                  handleSelectedItems={handleSelectedUserChange}
                  hasOnlyOurUsers
                  hasSingleSelection
                  disabled={isSaving}
                />
              </Field>
              <Field
                label="Portfolios"
                required
                validationState={fieldErrors.portfolios ? "error" : "none"}
                validationMessage={fieldErrors.portfolios}
                className={styles.dialogField}
              >
                <Dropdown
                  multiselect
                  placeholder={
                    isPortfoliosLoading
                      ? "Loading portfolios..."
                      : "Select portfolios..."
                  }
                  value={portfolioIds
                    .map(
                      (id) =>
                        portfolios.find((portfolio) => portfolio.id === id)
                          ?.label,
                    )
                    .filter((label): label is string => Boolean(label))
                    .join(", ")}
                  selectedOptions={portfolioIds}
                  onOptionSelect={(_e, data) =>
                    handlePortfolioIdsChange(data.selectedOptions)
                  }
                  className={dropdownStyles.dropdown}
                  listbox={{ className: dropdownStyles.listbox }}
                  positioning="below"
                  disabled={isSaving || isPortfoliosLoading}
                >
                  {sortedPortfolios.map((portfolio) => (
                    <Option
                      key={portfolio.id}
                      value={portfolio.id}
                      text={portfolio.label}
                    >
                      {portfolio.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular aria-hidden="true" />}
            onClick={() => void handleSave()}
            disabled={isSaveDisabled}
            tooltip="Save"
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
